/**
 * TodaysActivationPill — small home-page card surfacing one of the 12
 * Reclaim Presence activations as a daily-rotating "Today's Awareness"
 * suggestion. Server picks the slug based on UTC day-of-year so every
 * member sees the same card all day and it rotates at midnight.
 *
 * Failure mode: API silently returns nothing → component renders null
 * so the home feed stays clean (no broken/empty card).
 */
import { useEffect, useState } from "react";
import { ChevronRight, Leaf, Headphones } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function TodaysActivationPill({ onOpen }) {
  const [card, setCard] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API}/api/presence/activations/today`, { cache: "no-store" });
        if (r.ok && !cancelled) setCard(await r.json());
      } catch (_) { /* silently bail */ }
    })();
    return () => { cancelled = true; };
  }, []);

  if (!card) return null;

  return (
    <button
      onClick={() => onOpen(card.slug)}
      className="w-full text-left rounded-2xl bg-gradient-to-br from-emerald-950/90 via-emerald-900/80 to-teal-950/90 ring-1 ring-emerald-700/30 hover:ring-emerald-600/60 px-5 py-4 flex items-center gap-4 shadow-lg shadow-emerald-950/30 transition-all"
      data-testid="todays-activation-pill"
    >
      <div className="w-11 h-11 rounded-xl bg-emerald-500/15 ring-1 ring-emerald-400/20 flex items-center justify-center shrink-0">
        <Leaf size={20} className="text-emerald-300" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-[10px] uppercase tracking-[0.25em] text-emerald-300/70 mb-0.5">
          Today&apos;s Awareness
        </p>
        <h3 className="text-white font-medium text-sm truncate">{card.title}</h3>
        <div className="flex items-center gap-1.5 mt-0.5 text-[11px] text-emerald-200/60">
          <Headphones size={11} />
          <span>{card.duration_label}</span>
          <span className="text-emerald-200/30">·</span>
          <span className="truncate">{card.subtitle}</span>
        </div>
      </div>
      <ChevronRight size={18} className="text-emerald-300/60 shrink-0" />
    </button>
  );
}
